/**
 * component-bundle-core.js — shared bundle engine (Slice 23).
 *
 * Loaded (defer, before the block JS) by every bundle parent block; the
 * window.theme.bundle guard dedupes when both blocks coexist (Rule J12).
 * The ONLY global surface is window.theme.bundle. No storage APIs.
 *
 * U1 scope (spec §2c): formatMoney, addItems, afterAdd, selectedVariantId,
 * the in-memory state map, the product:variant-changed restore + restamp.
 * U3 added the popup engine: ensureOverlay (single-flight lazy inject of
 * component-overlay.js + .css — both assets, U3-H1), openBundle (move-node
 * open via theme.overlay + the bundle:popup-opened refresh dispatch — the
 * overlay emits no event on its moveId path, S23-P3-PLAN CH4), the
 * delegated openers (trigger card / data-bundle-open / #ID anchors),
 * capture-phase ATC click/keydown interception (one layer before the
 * theme's own capture submit handler — U3-H1), a real
 * closeBundleOverlays(), and design-mode block-select open/deferred-close.
 * ALL popup selectors are [data-bundle-pop-shell]-scoped — inline roots
 * also carry data-trigger/data-popup-id for byte-identity (CH2) and must
 * never match.
 * U3-H2: ATC-opened popups (data-bq-opened-by="atc" stamped per open)
 * perform the ORIGINAL add on ANY dismissal via one capture 'close'
 * listener + one-shot interceptor bypass; programmatic closes are excluded
 * by a suppress counter, design-mode closes by the designMode gate. The
 * [data-bq-continue] link just closes — same single pipeline adds.
 *
 * Delegations (plan amendments 1 + 2):
 *  - formatMoney -> window.theme.utilities.formatMoney (lazy lookup,
 *    minimal inline fallback). Block A never formats money in JS — it copies
 *    the children's server-formatted data-f-* strings via textContent.
 *  - addItems -> theme.cart_api.add(items, null, {suppressAutoOpen: true})
 *    (keeps the header badge live via cart:updated; one fetch, Shopify error
 *    description surfaces in the rejection).
 *
 * Unit-word stamping (C8a): a child block cannot read its parent block's
 * settings, so children render empty <span data-bq-unit="s|p"> spans and the
 * parent root carries data-bq-unit-s / data-bq-unit-p; the init stamp pass +
 * the variant-changed restamp fill them via textContent.
 */

(function () {
  'use strict';

  window.theme = window.theme || {};
  if (window.theme.bundle) return;

  var state = {};

  function formatMoney(cents, format) {
    var utils = window.theme.utilities;
    if (utils && typeof utils.formatMoney === 'function') return utils.formatMoney(cents, format);
    var amt = (cents / 100).toFixed(2).replace('.', ',');
    if (format && format.indexOf('{{') !== -1) { return format.replace(/\{\{\s*amount[^}]*\}\}/g, amt); }
    return amt + '\u00A0\u20AC';
  }

  function addItems(items) {
    var api = window.theme.cart_api;
    if (!api || typeof api.add !== 'function') {
      return Promise.reject(new Error('Cart unavailable'));
    }
    return api.add(items, null, { suppressAutoOpen: true });
  }

  /**
   * After-add matrix (slice-23 §2.6, reworked U1-H2 — native code application):
   *  code (any target)  -> GET cart, union existing discount-code titles with
   *                        the tier code, theme.cart_api.updateDiscount(joined)
   *                        (its cart:updated dispatch refreshes the drawer),
   *                        then honor the target below. Network/HTTP failure
   *                        -> FALLBACK to /discount/CODE?redirect= navigation.
   *                        "Code attached but no discount applied" is NOT a
   *                        failure — code validity is merchant responsibility.
   *  drawer             -> explicit cart:open dispatch (merchant intent beats
   *                        the ATC-auto-open toggle); cart_type 'page' has no
   *                        drawer -> /cart navigation instead.
   *  cart / checkout    -> location /cart | /checkout.
   */
  function finishTarget(target, cartType) {
    if (target === 'drawer') {
      if (cartType === 'page') {
        location.href = '/cart';
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:open'));
      return;
    }
    location.href = target === 'checkout' ? '/checkout' : '/cart';
  }

  function navigateDiscount(code, target) {
    var redirect = target === 'checkout' ? '/checkout' : '/cart';
    location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect);
  }

  function afterAdd(code, target, root) { // eslint-disable-line no-unused-vars
    target = target || 'cart';
    var cartType = window.theme.settings && window.theme.settings.cart_type;
    if (!code) {
      finishTarget(target, cartType);
      return;
    }
    var api = window.theme.cart_api;
    if (!api || typeof api.updateDiscount !== 'function' || typeof api.get !== 'function') {
      navigateDiscount(code, target);
      return;
    }
    api
      .get()
      .then(function (cart) {
        // cart/update.js {discount} REPLACES every applied code, so union the
        // existing code-type applications (type 'discount_code' — 'automatic'/
        // 'script' kinds aren't resubmittable codes; an absent/unmatched type
        // contributes nothing, leaving only the tier code at the residual risk
        // of clobbering an applied code we couldn't identify) with the tier
        // code, deduped case-insensitively (titles echo codes — see
        // component-cart-blocks.js spin precedent + cart-discount-code.liquid).
        var codes = [];
        var hasTier = false;
        var tierLower = code.toLowerCase();
        var apps = (cart && cart.cart_level_discount_applications) || [];
        apps.forEach(function (app) {
          if (!app || app.type !== 'discount_code' || !app.title) return;
          codes.push(app.title);
          if (app.title.toLowerCase() === tierLower) hasTier = true;
        });
        if (!hasTier) codes.push(code);
        return api.updateDiscount(codes.join(','));
      })
      .then(function () {
        finishTarget(target, cartType);
      })
      .catch(function () {
        navigateDiscount(code, target);
      });
  }

  /**
   * Resolve the product-info zone for a bundle root (U3-H1 #3): an up-tree
   * walk fails once the pop-body has been MOVED into the dialog (a body-end
   * sibling of the zone) — resolve via the block's shell instead, which
   * never leaves the zone; last resort: the page's first zone.
   */
  function infoZoneFor(root) {
    var zone = root.closest('[data-product-info-root]');
    if (zone) return zone;
    if (root.id) {
      var shell = document.querySelector('[data-bundle-pop-shell][data-pop-target="' + attrEscape(root.id) + '"]');
      zone = shell && shell.closest('[data-product-info-root]');
      if (zone) return zone;
    }
    return document.querySelector('[data-product-info-root]');
  }

  function selectedVariantId(root) {
    var zone = infoZoneFor(root);
    var blob = zone && zone.querySelector('[data-selected-variant-json]');
    if (!blob) return null;
    try {
      return JSON.parse(blob.textContent).id;
    } catch (e) {
      return null;
    }
  }

  /* ---------- popup engine (U3) ---------- */

  /**
   * Single-flight lazy inject of the overlay primitive — BOTH assets
   * (U3-H1 #1): the script from the shell's data-overlay-src and the
   * stylesheet from window.theme.assets.overlayCss (js-variables.liquid),
   * mirroring component-loader's overlay surface (assetKeys
   * ['overlayCss','overlayJs'] -> injectStylesheet: plain <link> to head).
   * Resolves when both have loaded (link.onload + script.onload), with a
   * ~2s fallback that resolves anyway — never deadlock the open. The link
   * is skipped when the stylesheet is already present (href match — e.g.
   * the loader's [data-overlay-trigger] path got there first); the
   * overlay's own window.theme.overlay guard makes script races harmless.
   */
  var overlayLoad = null;

  function overlayCssPresent(url) {
    var links = document.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].href === url || links[i].getAttribute('href') === url) return true;
    }
    return false;
  }

  function ensureOverlay(src) {
    if (window.theme.overlay) return Promise.resolve();
    if (!overlayLoad) {
      overlayLoad = new Promise(function (resolve) {
        var pending = 0;
        var settled = false;
        function done() {
          pending -= 1;
          if (!settled && pending <= 0) {
            settled = true;
            resolve();
          }
        }
        setTimeout(function () {
          if (!settled) {
            settled = true;
            resolve();
          }
        }, 2000);

        var cssUrl = window.theme.assets && window.theme.assets.overlayCss;
        if (cssUrl && !overlayCssPresent(cssUrl)) {
          pending += 1;
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssUrl;
          link.onload = done;
          link.onerror = done;
          document.head.appendChild(link);
        }

        pending += 1;
        var script = document.createElement('script');
        script.src = src;
        script.defer = true;
        script.onload = done;
        script.onerror = function () {
          overlayLoad = null; // DEV-181 retry reset — a hard load failure clears the single-flight latch so the next open re-injects (popup was dead until reload)
          done();
        };
        document.head.appendChild(script);
      });
    }
    return overlayLoad;
  }

  // Attribute-value escape for the popup-id lookup (mirrors the overlay's
  // minimal cssEscape — quotes/backslashes are all an attr string needs).
  function attrEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(String(value));
    }
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function shellOverlayId(shell) {
    var target = shell.getAttribute('data-pop-target');
    return target ? 'bundle-' + target.replace('bundle-pop-', '') : null;
  }

  /**
   * Open a popup-mode block from its shell. The overlay's moveId path is
   * fully synchronous (showModal + move-node), so the refresh dispatch can
   * follow open() directly: the block JS re-runs its measurements on the
   * now-visible body (vcap/arrows/panel all measured 0 while hidden).
   * `trigger` (when an element) rides the primitive's focus-restore-on-close.
   * `source` (U3-H2): 'atc' stamps data-bq-opened-by="atc" on the body —
   * re-stamped/cleared EVERY open (the body persists across opens) — which
   * arms the dismissal pipeline + shows the continue link (CSS-gated).
   */
  function openBundle(shell, trigger, source) {
    var target = shell.getAttribute('data-pop-target');
    if (!target) return;
    ensureOverlay(shell.getAttribute('data-overlay-src'))
      .then(function () {
        // ensureOverlay resolves on the ~2s fallback even when the script
        // failed — bail rather than throw into the catch.
        if (!window.theme.overlay) return;
        window.theme.overlay.open({
          id: shellOverlayId(shell),
          display: 'popup',
          size: 'md',
          title: shell.getAttribute('data-popup-title') || '',
          moveId: target,
          trigger: trigger || shell.querySelector('[data-bq-trigger-card]') || undefined
        });
        var body = document.getElementById(target);
        if (body) {
          if (source === 'atc') {
            body.setAttribute('data-bq-opened-by', 'atc');
          } else {
            body.removeAttribute('data-bq-opened-by');
          }
          document.dispatchEvent(new CustomEvent('bundle:popup-opened', { detail: { root: body } }));
        }
      })
      .catch(function () {});
  }

  /**
   * Close every open bundle overlay (spec §5). Called FIRST by the
   * product:variant-changed listener (before restore/restamp) and by the
   * block JS before the after-add matrix runs. The primitive's
   * restore-on-close is detachment-safe after an SRA swap (plan CH5) —
   * the discarded stale body is the accepted outcome (§2.5).
   *
   * U3-H2: programmatic closes must NOT run the ATC-dismissal add — the
   * suppress COUNTER is incremented per dialog closed here (close events
   * are queued/async, a boolean would race) and consumed by the capture
   * close listener below. Covers the popup-CTA-add close and the
   * variant-change close; the editor's deferred close is excluded by the
   * dismissal listener's designMode gate instead.
   */
  var suppressDismissal = 0;

  function closeBundleOverlays() {
    var overlay = window.theme.overlay;
    if (!overlay) return; // never loaded -> nothing can be open
    document.querySelectorAll('dialog.overlay[data-overlay-id^="bundle-"][open]').forEach(function (dialog) {
      suppressDismissal += 1;
      overlay.close(dialog.dataset.overlayId);
    });
  }

  /* Openers (delegated, spec §5): trigger card; [data-bundle-open="ID"]; and
     a[href="#ID"] where ID matches a popup shell's non-empty data-popup-id
     (custom-ID path active in EVERY popup trigger mode). Anchors with no
     matching shell fall through untouched. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    // U3-H2 continue link: just close the dialog WITHOUT the suppress
    // counter — the dismissal listener performs the add (ONE add path).
    var cont = e.target.closest('[data-bq-continue]');
    if (cont) {
      var contDialog = cont.closest('dialog.overlay');
      if (contDialog && window.theme.overlay) window.theme.overlay.close(contDialog.dataset.overlayId);
      return;
    }
    var card = e.target.closest('[data-bq-trigger-card]');
    if (card) {
      var cardShell = card.closest('[data-bundle-pop-shell]');
      if (cardShell) openBundle(cardShell, card);
      return;
    }
    var opener = e.target.closest('[data-bundle-open], a[href^="#"]');
    if (!opener) return;
    var id = opener.hasAttribute('data-bundle-open')
      ? opener.getAttribute('data-bundle-open')
      : opener.getAttribute('href').slice(1);
    if (!id) return;
    var shell = document.querySelector('[data-bundle-pop-shell][data-popup-id="' + attrEscape(id) + '"]');
    if (!shell) return;
    e.preventDefault();
    openBundle(shell, opener);
  });

  /* ATC interception (U3-H1 #2): one layer EARLIER than submit — the theme's
     own handleBuyButtonsSubmit is capture-phase and registered before us
     (component-product-info.js:374-376 matches form.pp-buy-buttons__form and
     preventDefaults immediately; registered at :576), so a bundle submit
     listener fires too late. Instead: capture-phase click on the form's
     submit control ([data-pp-atc] — the same control that flow drives) +
     capture-phase Enter inside the form (covers Enter-in-qty-input submits).
     Claiming shell: first atc shell within the form's
     [data-product-info-root], else first in document. Express-checkout
     buttons ({{ form | payment_button }}) are not [data-pp-atc] and never
     keydown-submit — untouched by construction. */
  function claimingAtcShell(form) {
    var zone = form.closest('[data-product-info-root]');
    return (
      (zone && zone.querySelector('[data-bundle-pop-shell][data-trigger="atc"]')) ||
      document.querySelector('[data-bundle-pop-shell][data-trigger="atc"]')
    );
  }

  /* U3-H2 one-shot bypass: set immediately before the dismissal pipeline's
     synthetic atcBtn.click() — the very next click event IS that synthetic
     one (same task, nothing can interleave); consuming it lets the native
     add flow proceed uninterercepted. Kept on the keydown too (belt-and-
     braces — a synthetic click never fires keydown). */
  var atcBypass = false;

  document.addEventListener('click', function (e) {
    if (atcBypass) {
      atcBypass = false;
      return;
    }
    if (!e.target.closest) return;
    var atc = e.target.closest('[data-pp-atc]');
    if (!atc) return;
    var form = atc.closest('form.pp-buy-buttons__form');
    if (!form) return;
    var shell = claimingAtcShell(form);
    if (!shell) return;
    e.preventDefault();
    e.stopPropagation();
    openBundle(shell, null, 'atc');
  }, true);

  document.addEventListener('keydown', function (e) {
    if (atcBypass) {
      atcBypass = false;
      return;
    }
    if (e.key !== 'Enter' || !e.target.closest) return;
    if (e.target.tagName === 'TEXTAREA') return;
    var form = e.target.closest('form.pp-buy-buttons__form');
    if (!form) return;
    var shell = claimingAtcShell(form);
    if (!shell) return;
    e.preventDefault();
    e.stopPropagation();
    openBundle(shell, null, 'atc');
  }, true);

  /* U3-H2 dismissal pipeline: an ATC-opened popup performs the ORIGINAL add
     on ANY dismissal (Esc / backdrop / close button / continue link). ONE
     document-level CAPTURE 'close' listener — close doesn't bubble; capture
     reaches it (same technique as the block JS scroll listener) and fires
     BEFORE the dialog's own restore-on-close listener, so the body is still
     inside the dialog. Programmatic closes consume the suppress counter and
     never add; design mode never adds. Accepted edge (documented, not
     solved): a FAILED popup-CTA add (error line) followed by a dismissal
     still fires the original single add. */
  document.addEventListener('close', function (e) {
    if (window.Shopify && Shopify.designMode) return;
    var dialog = e.target;
    if (!dialog || !dialog.matches || !dialog.matches('dialog.overlay[data-overlay-id^="bundle-"]')) return;
    if (suppressDismissal > 0) {
      suppressDismissal -= 1;
      return;
    }
    // U7 sanctioned core edit (slice-23 §19.4): A-scoped -> A+B so a B ATC popup resolves+dismisses too.
    var body = dialog.querySelector('[data-bundle-qty], [data-bundle-prods]');
    if (!body || body.getAttribute('data-bq-opened-by') !== 'atc') return;
    body.removeAttribute('data-bq-opened-by');
    // Resolve the original control: body -> shell -> zone -> form -> button.
    // Every lookup null-safe: anything missing -> just don't add.
    var shell = body.id && document.querySelector('[data-bundle-pop-shell][data-pop-target="' + attrEscape(body.id) + '"]');
    var zone = shell && shell.closest('[data-product-info-root]');
    var form = zone && zone.querySelector('form.pp-buy-buttons__form');
    var atcBtn = form && form.querySelector('[data-pp-atc]');
    if (!atcBtn) return;
    atcBypass = true;
    atcBtn.click();
  }, true);

  /* Editor support (design mode only, spec §5): block select opens the
     popup so the merchant can edit tiers live. Deselect close is DEFERRED
     one tick (plan §9 A2): moving selection parent -> tier fires
     deselect(parent) then select(tier), and the tier lives inside the
     DIALOG at that point — the same-tick select (shell OR dialog scoped)
     cancels the pending close so tier editing never flickers shut.
     Settings-rerender quirks accepted v1 (plan CH7 — self-stabilizing). */
  if (window.Shopify && Shopify.designMode) {
    var pendingClose = {};

    document.addEventListener('shopify:block:select', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var shell = t.closest('[data-bundle-pop-shell]');
      if (shell) {
        var id = shellOverlayId(shell);
        if (id && pendingClose[id]) {
          clearTimeout(pendingClose[id]);
          pendingClose[id] = null;
        }
        openBundle(shell);
        return;
      }
      var dialog = t.closest('dialog.overlay[data-overlay-id^="bundle-"]');
      if (dialog) {
        var did = dialog.dataset.overlayId;
        if (pendingClose[did]) {
          clearTimeout(pendingClose[did]);
          pendingClose[did] = null;
        }
        // already open — selecting inside the dialog needs no open
      }
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var id = null;
      var shell = t.closest('[data-bundle-pop-shell]');
      if (shell) {
        id = shellOverlayId(shell);
      } else {
        var dialog = t.closest('dialog.overlay[data-overlay-id^="bundle-"]');
        if (dialog) id = dialog.dataset.overlayId;
      }
      if (!id) return;
      if (pendingClose[id]) clearTimeout(pendingClose[id]);
      pendingClose[id] = setTimeout(function () {
        pendingClose[id] = null;
        if (window.theme.overlay) window.theme.overlay.close(id);
      }, 0);
    });
  }

  function selectedTier(root) {
    return root.querySelector('[data-bqt].is-selected') || root.querySelector('[data-bqt]');
  }

  function stampUnits(root) {
    var singular = root.getAttribute('data-bq-unit-s') || '';
    var plural = root.getAttribute('data-bq-unit-p') || '';
    root.querySelectorAll('[data-bq-unit]').forEach(function (span) {
      span.textContent = span.getAttribute('data-bq-unit') === 'p' ? plural : singular;
    });
  }

  /** Stamp the CTA amount + savings line from the selected tier's data-f-*. */
  function restamp(root) {
    stampUnits(root);
    var tier = selectedTier(root);
    if (!tier) return;
    var amount = root.querySelector('[data-bq-cta-amount]');
    if (amount) amount.textContent = ' · ' + (tier.getAttribute('data-f-now') || '');
    var save = root.querySelector('.bundle-qty__save');
    if (save) {
      var discount = parseInt(tier.getAttribute('data-discount'), 10) || 0;
      var saveAmount = save.querySelector('[data-bq-save-amount]');
      var savePct = save.querySelector('[data-bq-save-pct]'); // F3: save-row −X% chip (4th sanctioned core edit, merchant-directed)
      if (discount > 0) {
        if (saveAmount) saveAmount.textContent = tier.getAttribute('data-f-save') || '';
        if (savePct) savePct.textContent = '−' + discount + '%';
        save.hidden = false;
      } else {
        save.hidden = true;
      }
    }
  }

  /**
   * Normalize selection to exactly one tier: the stored state index (SRA
   * restore) wins, then the first server-marked .is-selected (first
   * bqt_preselected — extras trimmed), then the first tier.
   */
  function normalize(root) {
    var tiers = root.querySelectorAll('[data-bqt]');
    if (!tiers.length) return;
    var chosen = null;
    var blockId = root.getAttribute('data-block-id');
    var saved = state[blockId] && typeof state[blockId].tier === 'number' ? state[blockId].tier : -1;
    if (saved > -1 && saved < tiers.length) chosen = tiers[saved];
    if (!chosen) chosen = root.querySelector('[data-bqt].is-selected') || tiers[0];
    tiers.forEach(function (tier) {
      var on = tier === chosen;
      tier.classList.toggle('is-selected', on);
      tier.setAttribute('aria-checked', on ? 'true' : 'false');
      tier.setAttribute('tabindex', on ? '0' : '-1');
    });
    restamp(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-bundle-qty]').forEach(normalize);
  }

  function boot() {
    initAll();
    // ONE warn at init (spec §5): two atc shells -> first-in-DOM wins.
    if (document.querySelectorAll('[data-bundle-pop-shell][data-trigger="atc"]').length > 1) {
      console.warn('[bundle] Multiple bundle blocks use the "atc" trigger — the first in the DOM wins.');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('product:variant-changed', function (e) {
    var root = e.detail && e.detail.root;
    if (!root) return;
    closeBundleOverlays();
    initAll(root);
  });

  window.theme.bundle = {
    formatMoney: formatMoney,
    addItems: addItems,
    afterAdd: afterAdd,
    selectedVariantId: selectedVariantId,
    state: state,
    restamp: restamp,
    closeBundleOverlays: closeBundleOverlays
  };
})();
